import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { RiCloseLargeLine } from "react-icons/ri";
import { editorStoreContext } from "../../store/editorStore";
import { userStoreContext } from "../../store/userStore";
import limitationScreen from "../../assets/Render5.png";

/**
 * Freemium paywall modal. Shown when a free-plan user hits one of the
 * floorplanner caps. Reasons:
 *   - "shapes":       user tried to add an item past the per-floorplan shape cap
 *   - "project":      user tried to create another floorplan past the count cap
 *   - "download":     user tried to download the floorplan (paid feature)
 *   - "addToProject": user tried to add the floorplan to a BONI project (paid)
 *
 * Visibility is driven by `editorStore.paywallVisible` and `paywallReason`.
 * The CTA navigates to the BONI subscription page on the main client app.
 *
 * Layout/styling mirrors the client's `SubscriptionLimitationsView`: a wide
 * yellow card with copy on the left and a hero render on the right.
 */
type PaywallReason = "shapes" | "project" | "download" | "addToProject";

const PaywallModal: React.FC = observer(() => {
  const { t } = useTranslation();
  const editorStore = useContext(editorStoreContext);
  const userStore = useContext(userStoreContext);

  if (!editorStore.paywallVisible) return null;

  const reason: PaywallReason = editorStore.paywallReason ?? "shapes";

  const limit =
    reason === "project"
      ? userStore.maxFloorplans ?? 1
      : userStore.maxFloorplanShapes ?? 50;

  const title =
    reason === "project"
      ? t("paywall.projectLimitTitle", { count: limit, limit })
      : reason === "shapes"
        ? t("paywall.shapesLimitTitle", { count: limit, limit })
        : reason === "download"
          ? t("paywall.downloadTitle")
          : t("paywall.addToProjectTitle");

  const subtitle =
    reason === "project"
      ? t("paywall.createMore")
      : reason === "shapes"
        ? t("paywall.keepBuilding")
        : reason === "download"
          ? t("paywall.download")
          : t("paywall.addToProject");

  const description =
    reason === "project"
      ? t("paywall.projectDescription")
      : reason === "shapes"
        ? t("paywall.shapesDescription")
        : reason === "download"
          ? t("paywall.downloadDescription")
          : t("paywall.addToProjectDescription");

  const clientUrl = process.env.REACT_APP_CLIENT_URI || "";
  const subscriptionUrl = `${clientUrl}/mypage/subscription`;

  const handleUpgrade = () => {
    window.location.href = subscriptionUrl;
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#13030399] pointer-events-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          editorStore.hidePaywall();
        }
      }}
    >
      <div className="relative w-[911px] max-w-[95vw] h-[356px] bg-bonigold rounded-[8px] overflow-hidden shadow-xl">
        <div className="flex flex-row relative h-full">
          <div className="flex flex-col items-center mt-[48px] w-[546px] max-w-full px-6">
            <div className="font-dm text-blackplum text-center w-[334px] max-w-full text-2xl leading-normal">
              {title}
            </div>
            <div className="font-dm font-bold text-blackplum mt-[8px] text-center leading-normal">
              {subtitle}
            </div>
            <div className="font-dm text-blackplum mt-[24px] text-center w-[422px] max-w-full leading-normal">
              {description}
            </div>
            <button
              type="button"
              onClick={handleUpgrade}
              className="w-[234px] h-[52px] mt-[32px] flex items-center justify-center font-dm text-lg rounded-lg bg-blackplum text-white hover:opacity-90 active:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-blackplum"
            >
              {t("paywall.startTrial")}
            </button>
          </div>

          <RightSideView />

          <button
            type="button"
            aria-label={t("paywall.close")}
            className="absolute top-[12px] right-[12px] z-10 w-9 h-9 flex items-center justify-center rounded-full text-blackplum hover:bg-black/10 active:bg-black/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blackplum"
            onClick={(e) => {
              editorStore.hidePaywall();
              e.stopPropagation();
            }}
          >
            <RiCloseLargeLine size={18} />
          </button>
        </div>
      </div>
    </div>
  );
});

const RightSideView: React.FC = () => {
  return (
    <div className="w-[365px] h-[356px] overflow-hidden">
      <img
        className="w-full h-full object-cover object-center"
        src={limitationScreen}
        alt=""
      />
    </div>
  );
};

export default PaywallModal;
